import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useLocation } from "react-router-dom";
import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useState, useRef, useCallback } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import aboutImage from "../assets/images/about-bg.jpg";
import { useUserParams } from "../hooks/useUserParams";

const useAnimatedBorder = () => {
  const topLeftRef = useRef(null);
  const topRightRef = useRef(null);
  const bottomLeftRef = useRef(null);
  const bottomRightRef = useRef(null);
  const rightRef = useRef(null);
  const leftRef = useRef(null);

  useEffect(() => {
    const timeouts = [];

    // Top borders animation
    timeouts.push(
      setTimeout(() => {
        topLeftRef.current?.classList.remove("w-0");
        topLeftRef.current?.classList.add("w-half");
        topRightRef.current?.classList.remove("w-0");
        topRightRef.current?.classList.add("w-half");
      }, 10)
    );

    // Side borders animation
    timeouts.push(
      setTimeout(() => {
        leftRef.current?.classList.remove("h-0");
        leftRef.current?.classList.add("h-full");
        rightRef.current?.classList.remove("h-0");
        rightRef.current?.classList.add("h-full");
      }, 1000)
    );

    // Bottom borders animation
    timeouts.push(
      setTimeout(() => {
        bottomLeftRef.current?.classList.remove("w-0");
        bottomLeftRef.current?.classList.add("w-half");
        bottomRightRef.current?.classList.remove("w-0");
        bottomRightRef.current?.classList.add("w-half");
      }, 4000)
    );

    return () => timeouts.forEach((timeout) => clearTimeout(timeout));
  }, []);

  return {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  };
};

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = useCallback(() => {
    setWindowSize(window.innerWidth);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, [onWindowResize]);

  return windowSize;
};

const useScrollVisibility = () => {
  const slideRefs = useRef([]);

  const addToRefs = (el) => {
    if (el && !slideRefs.current.includes(el)) {
      slideRefs.current.push(el);
    }
  };

  const isScrolledIntoView = useCallback((elem) => {
    if (elem) {
      const rect = elem.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      return elemTop >= 0 && elemBottom <= window.innerHeight;
    }
    return false;
  }, []);

  useEffect(() => {
    slideRefs.current.forEach((el) => {
      if (isScrolledIntoView(el)) {
        el?.classList.remove("slide-left");
      }
    });
  }, [isScrolledIntoView]);

  return { addToRefs };
};

export default function About() {
  const windowSize = useWindowSize();
  const {
    topLeftRef,
    topRightRef,
    bottomLeftRef,
    bottomRightRef,
    rightRef,
    leftRef,
  } = useAnimatedBorder();
  const { addToRefs } = useScrollVisibility();
  const {
    getCurrentAgentName,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
    getCurrentQuote,
    getCurrentAbout,
    getCurrentHeadshot,
  } = useUserParams();

  const agentName = getCurrentAgentName();
  const agentNumber = getCurrentAgentNumber();
  const agentEmail = getCurrentAgentEmail();
  const brokerage = getCurrentBrokerage();
  const quote = getCurrentQuote();
  const aboutText = getCurrentAbout();
  const headshot = getCurrentHeadshot();

  const seoConfig = pageSEO.about();

  return (
    <>
      <SEO {...seoConfig} />
      <div className="about-section-bg w-full p-8 md:p-40 flex justify-center"
        style={{
          background: `url(${constants.ABOUT_IMAGE}) no-repeat center center fixed`,
          backgroundSize: 'cover',
        }}
      >
        <div
          className="about-section-content flex flex-col lg:flex-row gap-y-10 gap-x-14 w-full md:max-w-[60vw] bg-white/80 backdrop-blur-md rounded-2xl p-10 shadow-lg"
          style={{
            border: constants.RURAL_BORDERS.double,
            boxShadow: constants.RURAL_BORDERS.shadow,
          }}
        >
          <img
            src={headshot}
            alt={agentName}
            width={400}
            height={400}
            className="rounded-2xl w-3/5 h-full self-center"
          />
          <div className="text-black text-center flex flex-col items-center justify-center w-full">
            <h1 className="font-mont text-4xl italic pb-6">
              &ldquo;{quote}&rdquo;
            </h1>
            <p className="mt-4 text-3xl font-mont font-bold">{agentName}</p>
            <p className="mt-2 text-2xl font-mont">{brokerage}</p>
            <p className="mt-6 text-xl font-mont text-gray-700 whitespace-pre-line">
              {aboutText || constants.ABOUT_DESCRIPTION}
            </p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <p className="text-xl font-mont">
                <span className="font-bold">Email:</span>{" "}
                <a
                  href={agentEmail ? `mailto:${agentEmail}` : undefined}
                  className="text-blue-700 underline"
                >
                  {agentEmail}
                </a>
              </p>
              <p className="text-xl font-mont">
                <span className="font-bold">Phone:</span>{" "}
                <a
                  href={agentNumber ? `tel:${agentNumber.replace(/[^\\d+]/g, "")}` : undefined}
                  className="text-blue-700 underline"
                >
                  {agentNumber}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
